import ReactDOM from 'react-dom/client';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import AuthProvider from '~/context/AuthProvider';
import { SessionExpiryProvider } from '~/context/SessionProvider';
import CampaignProvider from '~/context/CampaignProvider';
import {
    QueryCache,
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query';
import './index.css';
// import './satoshi.css';
import './simple-datatables.css';
import Root from './routes/root.js';
import Login from './routes/login.js';
import ProtectedPage from './components/ProtectedPage.js';
import CampaignRequests from './pages/Campaigns/CampaignRequests.js';
import CampaignRequestDetail, {
    loader as campaignRequestDetailLoader,
} from './pages/Campaigns/CampaignRequestDetail.js';
import FundsRaisedSync from './pages/Finance/FundsRaisedSync.js';
import FinancialSettings from './pages/Finance/FinancialSettings.tsx';
import CampaignMessage from './pages/Settings/CampaignMessage.tsx';
import Payouts from './pages/Finance/Payouts.tsx';
import PayoutDetail from './pages/Finance/PayoutDetail.tsx';
import Dashboard from './pages/Dashboard/index.tsx';
import MedicalCenterDetail, {
    loader as medicalCenterDetailLoader,
} from './pages/Outreach/MedicalCenterDetail.tsx';
import Vendors from './pages/Outreach/Vendors.tsx';
import VendorDetail from './pages/Outreach/VendorDetail.tsx';
import Users from './pages/Users/Users.tsx';
import MedicalProfessionals from './pages/Outreach/MedicalProfessionals.tsx';
import VerifyClient from './pages/MedicalProfessional/VerifyClient.tsx';
import Clients from './pages/MedicalProfessional/Clients.tsx';
import MedicalProfessionalDetail, {
    loader as medicalProfessionalDetailLoader,
} from './pages/Outreach/MedicalProfessionalDetail.tsx';
import UserProfile from './pages/Users/UserProfile.tsx';
import ClientCampaigns from './pages/Campaigns/ClientCampaigns.tsx';
import NewCampaigns from './pages/Campaigns/NewCampaigns.tsx';
import CampaignNotifications from './pages/Campaigns/CampaignNotifications.tsx';
import UnMonitoredChanges from './pages/Campaigns/UnMonitoredChanges.tsx';
import NonClientCampaigns from './pages/Campaigns/NonClientCampaigns.tsx';
import Messages from './pages/Messages/index.tsx';
import AppliedGrants from './pages/AppliedGrants/index.tsx';
import GrantSettings from './pages/AppliedGrants/GrantSettings.tsx';
import UniqueDonors from './pages/Campaigns/UniqueDonors.tsx';
import ReportFiltersProvider from './context/ReportFiltersProvider.tsx';
import NewReport from './pages/Reports/NewReport.tsx';
import CampaignSearchResults from './pages/Reports/CampaignSearchResults.tsx';
import CampaignApplicationSearchResults from './pages/Reports/CampaignApplicationSearchResults.tsx';
import CampaignRequestSearchResults from './pages/Reports/CampaignRequestSearchResults.tsx';
import TeamMemberSearchResults from './pages/Reports/TeamMemberSearchResults.tsx';
import FundRequestSearchResults from './pages/Reports/FundRequestSearchResults.tsx';
import StatusDurationResults from './pages/Reports/StatusDurationResults.tsx';
import MonthlyCampaignProfilesResults from './pages/Reports/predefined/MonthlyCampaignProfilesResults.tsx';
import CampaignRequestTimelineResults from './pages/Reports/predefined/CampaignRequestTimelineResults.tsx';
import CampaignOpenToDisbursableResults from './pages/Reports/predefined/CampaignOpenToDisbursableResults.tsx';
import CampaignLastActivityResults from './pages/Reports/predefined/CampaignLastActivityResults.tsx';
import MedProsNeedingEmailResults from './pages/Reports/predefined/MedProsNeedingEmailResults.tsx';
import TotalFundsRaisedByMedCenterResults from './pages/Reports/predefined/TotalFundsRaisedByMedCenterResults.tsx';
import MonthlyCRStatsResults from './pages/Reports/predefined/MonthlyCRStatsResults.tsx';
import RefundAuditResults from './pages/Reports/predefined/RefundAuditResults.tsx';
import SavedReports from './pages/Reports/SavedReports.tsx';
import MedicalCenters from './pages/Outreach/MedicalCenters.tsx';
import Files from './pages/Files/Files.tsx';
import FundRequests from './pages/FundRequests/FundRequests/FundRequests.tsx';
import CampaignDetail from './pages/Campaigns/CampaignDetail.tsx';
import OneTimeContributions from './pages/OneTimeContributions/index.tsx';
import RecurringContributions from './pages/OneTimeContributions/RecurringContributions.tsx';
import FundRequestDetail from './pages/FundRequests/FundRequestDetail.tsx';
import toast, { Toaster } from 'react-hot-toast';
import { FundRequestContextProvider } from './context/FundRequestProvider';
import { FundRequestPDF } from './pages/FundRequests/FundRequestPDF.tsx';
import RequestChanges from './pages/FundRequests/RequestChanges/RequestChanges.tsx';
import RequestChangeDetail from './pages/FundRequests/RequestChanges/RequestChangeDetail.tsx';
import { UserContextProvider } from './context/UserProvider.jsx';
import React from 'react';
import CampaignApplicationDetail from './pages/Campaigns/CampaignApplicationDetail.tsx';
import CampaignApplications from './pages/Campaigns/CampaignApplications.tsx';
import AdminResetPassword from './pages/Authentication/AdminResetPassword.tsx';
import ForgotPassword from './pages/Authentication/ForgotPassword.tsx';
import SetPassword from './pages/Authentication/SetPassword.tsx';
import WebhookErrors from './pages/WebhookErrors';

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
    queryCache: new QueryCache({
        onError: (error: any, query) => {
            console.log('error at queryClient: ', error);
            // Handle 401 unauthorized errors by navigating to login
            if (error.response?.status === 401) {
                console.log('Unauthorized access, redirecting to login');
                toast.error('Unauthorized access, redirecting to login');
                // Clear auth data from localStorage
                localStorage.removeItem('auth');
                localStorage.removeItem('user');
                // Redirect to login page
                window.location.href = '/login';
            } else {
                // For other errors, show toast notification
                toast.error(error.message);
            }
        },
    }),
});

const AppRoot = () => {
    const router = createBrowserRouter([
        {
            path: '/',
            element: <Root />,
            errorElement: <Login />,
            children: [
                {
                    path: 'login',
                    element: <Login />,
                },
                {
                    path: 'reset-password',
                    element: <AdminResetPassword />,
                },
                {
                    path: 'forgot-password',
                    element: <ForgotPassword />,
                },
                {
                    path: 'set-password',
                    element: <SetPassword />,
                },
                {
                    path: '/',
                    element: <ProtectedPage />,
                    children: [
                        {
                            path: '/',
                            element: <Dashboard />,
                        },
                        {
                            path: 'client-campaigns',
                            element: <ClientCampaigns />,
                        },
                        {
                            path: 'non-client-campaigns',
                            element: <NonClientCampaigns />,
                        },
                        {
                            path: 'new-campaigns',
                            element: <NewCampaigns />,
                        },
                        {
                            path: 'campaign-notifications',
                            element: <CampaignNotifications />,
                        },
                        {
                            path: 'un-monitored-changes',
                            element: <UnMonitoredChanges />,
                        },
                        {
                            path: 'campaigns/:campaign_id',
                            element: <CampaignDetail />,
                        },
                        {
                            path: 'campaign-applications',
                            element: <CampaignApplications />,
                        },
                        {
                            path: 'campaign-applications/:application_id',
                            element: <CampaignApplicationDetail />,
                        },
                        {
                            path: 'campaigns/:id/unique-donors',
                            element: <UniqueDonors />,
                        },
                        {
                            path: 'messages',
                            element: <Messages />,
                        },
                        {
                            path: 'medical-professionals',
                            element: <MedicalProfessionals />,
                        },
                        {
                            path: 'medical-professionals/:id',
                            element: <MedicalProfessionalDetail />,
                            loader: medicalProfessionalDetailLoader,
                        },
                        {
                            path: 'verify/:patientId',
                            element: <VerifyClient />,
                        },
                        {
                            path: 'clients',
                            element: <Clients />,
                        },
                        {
                            path: 'medical-centers',
                            element: <MedicalCenters />,
                        },
                        {
                            path: 'medical-centers/:id',
                            element: <MedicalCenterDetail />,
                            loader: medicalCenterDetailLoader,
                        },
                        {
                            path: 'vendors-organizations',
                            element: <Vendors />,
                        },
                        {
                            path: 'vendors-organizations/:id',
                            element: <VendorDetail />,
                        },
                        {
                            path: 'campaign-requests',
                            element: <CampaignRequests />,
                        },
                        {
                            path: 'campaign-requests/:id',
                            element: <CampaignRequestDetail />,
                            loader: campaignRequestDetailLoader,
                        },
                        {
                            path: 'synchronize-data',
                            element: <FundsRaisedSync />,
                        },
                        {
                            path: 'payouts',
                            element: <Payouts />,
                        },
                        {
                            path: 'payouts/:payout_id',
                            element: <PayoutDetail />,
                        },
                        {
                            path: 'applied-grants',
                            element: <AppliedGrants />,
                        },
                        {
                            path: 'grant-settings',
                            element: <GrantSettings />,
                        },
                        {
                            path: 'financial-settings',
                            element: <FinancialSettings />,
                        },
                        {
                            path: 'campaign-message-settings',
                            element: <CampaignMessage />,
                        },
                        {
                            path: 'users',
                            element: <Users />,
                        },
                        {
                            // /staff was a second copy of the users list that
                            // never filtered out clients. It now redirects to
                            // the real one with the staff filter on
                            // (HHLTB-2665), so old links and bookmarks still
                            // land somewhere useful.
                            path: 'staff',
                            element: <Navigate to='/users?staff_only=true' replace />,
                        },
                        {
                            path: 'users/:id',
                            element: (
                                <UserContextProvider>
                                    <UserProfile />
                                </UserContextProvider>
                            ),
                        },
                        {
                            path: 'new-report',
                            element: <NewReport />,
                        },
                        {
                            path: 'reports/results',
                            element: <CampaignSearchResults />,
                        },
                        {
                            path: 'reports/campaign-applications',
                            element: <CampaignApplicationSearchResults />,
                        },
                        {
                            path: 'reports/campaign-requests',
                            element: <CampaignRequestSearchResults />,
                        },
                        {
                            path: 'reports/team-members',
                            element: <TeamMemberSearchResults />,
                        },
                        {
                            path: 'reports/fund-requests',
                            element: <FundRequestSearchResults />,
                        },
                        {
                            path: 'reports/status-duration',
                            element: <StatusDurationResults />,
                        },
                        {
                            path: 'reports/predefined/monthly-campaign-profiles/results',
                            element: <MonthlyCampaignProfilesResults />,
                        },
                        {
                            path: 'reports/predefined/med-pros-needing-email/results',
                            element: <MedProsNeedingEmailResults />,
                        },
                        {
                            path: 'reports/predefined/total-funds-raised-by-med-center/results',
                            element: <TotalFundsRaisedByMedCenterResults />,
                        },
                        {
                            path: 'reports/predefined/monthly-cr-stats/results',
                            element: <MonthlyCRStatsResults />,
                        },
                        {
                            path: 'reports/predefined/campaign-request-timeline/results',
                            element: <CampaignRequestTimelineResults />,
                        },
                        {
                            path: 'reports/predefined/campaign-open-to-disbursable/results',
                            element: <CampaignOpenToDisbursableResults />,
                        },
                        {
                            path: 'reports/predefined/campaign-last-activity/results',
                            element: <CampaignLastActivityResults />,
                        },
                        {
                            path: 'reports/predefined/refund-audit/results',
                            element: <RefundAuditResults />,
                        },
                        {
                            path: 'saved-reports',
                            element: <SavedReports />,
                        },
                        {
                            path: 'files',
                            element: <Files />,
                        },
                        {
                            path: 'fund-requests',
                            element: (
                                <FundRequestContextProvider>
                                    <FundRequests />
                                </FundRequestContextProvider>
                            ),
                        },
                        {
                            path: 'fund-requests/:id',
                            element: (
                                <FundRequestContextProvider>
                                    <FundRequestDetail />
                                </FundRequestContextProvider>
                            ),
                        },
                        {
                            path: 'fund-requests/:id/pdf',
                            element: <FundRequestPDF />,
                        },
                        {
                            path: 'payees/request-changes',
                            element: (
                                <FundRequestContextProvider>
                                    <RequestChanges />
                                </FundRequestContextProvider>
                            ),
                        },
                        {
                            path: 'payees/request-changes/:id',
                            element: (
                                <FundRequestContextProvider>
                                    <RequestChangeDetail />
                                </FundRequestContextProvider>
                            ),
                        },
                        {
                            path: 'one-time-contributions',
                            element: <OneTimeContributions />,
                        },
                        {
                            path: 'recurring-contributions',
                            element: <RecurringContributions />,
                        },
                        {
                            path: 'webhook-errors',
                            element: <WebhookErrors />,
                        },
                    ],
                },
            ],
        },
    ]);

    return <RouterProvider router={router} />;
};
// Conditionally wrap app with StrictMode based on environment
const isProduction = import.meta.env.PROD;
const isDevelopment = import.meta.env.DEV;

// Enable StrictMode in production and staging, but not in local development
const shouldUseStrictMode =
    isProduction ||
    (isDevelopment && import.meta.env.VITE_DJANGO_URL?.includes('staging'));

const AppWithProviders = (
    <AuthProvider>
        <SessionExpiryProvider>
            <CampaignProvider>
                <ReportFiltersProvider>
                    <QueryClientProvider client={queryClient}>
                        <Toaster position='top-right' />
                        <AppRoot />
                    </QueryClientProvider>
                </ReportFiltersProvider>
            </CampaignProvider>
        </SessionExpiryProvider>
    </AuthProvider>
);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    shouldUseStrictMode ? (
        <React.StrictMode>{AppWithProviders}</React.StrictMode>
    ) : (
        AppWithProviders
    )
);
